// src/hooks/useUploadAsset.ts
import { useState, useCallback, useEffect } from "react";
import { Vehicle } from "../../state/types";

export interface MediaFile {
  file: File;
  mediaGroup: string;
}

export interface UploadState {
  vehicle: Vehicle;
  uploadedFiles: MediaFile[];
}

const createDefaultVehicle = (assetIdentifier: string = 'New Vehicle'): Vehicle => ({
  assetIdentifier,
  id: '',
  clientId: '',
  image_dir: '',
  config: '',
  pushed: false,
  ready: false,
  vin: assetIdentifier,
  created_at: new Date().toISOString(),
  exterior: 0,
  interior: 0,
  manual: 0,
  thumbnail: '',
  status: null,
  assetId: assetIdentifier,
  pk: '',
  sk: '',
  imageExterior: [],
  imageManual: [],
  imagePano: [],
  imageFeatured: [],
  imageMask: [],
  imageCrop: [],
  imageUndercarriage: [],
  imageUndercarriageDewarp: [],
  imageBgr: [],
  archived: false,
  processingStatus: "NOT STARTED",
  syndicationStatus: "NOT STARTED",
});

interface UseUploadAssetProps {
  initialVehicle?: Partial<Vehicle>;
  onStateChange?: (state: UploadState) => void;
  onSave?: (state: UploadState) => Promise<void>;
}

export const useUploadAsset = ({ initialVehicle, onStateChange, onSave }: UseUploadAssetProps) => {
  const getInitialState = useCallback((): Vehicle => {
    if (initialVehicle) {
      const defaults = createDefaultVehicle(initialVehicle.assetIdentifier);
      return { ...defaults, ...initialVehicle };
    }
    return createDefaultVehicle();
  }, [initialVehicle]);

  const [vehicle, setVehicle] = useState<Vehicle>(getInitialState);
  const [uploadedFiles, setUploadedFiles] = useState<MediaFile[]>([]);
  const [currentImageType, setCurrentImageType] = useState<string>("imageExterior");
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info" as "info" | "success" | "error",
  });

  useEffect(() => {
    setVehicle(getInitialState());
    setUploadedFiles([]);
  }, [getInitialState]);

  useEffect(() => {
    onStateChange?.({ vehicle, uploadedFiles });
  }, [vehicle, uploadedFiles, onStateChange]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length || !currentImageType) return;
    const file = event.target.files[event.target.files.length - 1];
    setUploadedFiles((prevFiles) => [...prevFiles, { file, mediaGroup: currentImageType }]);

    const objectUrl = URL.createObjectURL(file);
    const newImagePreview = {
      imageUrlCf: objectUrl,
      imageTag: `New Image`,
      isNew: true,
    };

    setVehicle(prevVehicle => {
      const currentImages = (prevVehicle[currentImageType as keyof Vehicle] as any[]) || [];
      const newImageList = [...currentImages, newImagePreview];
      return { ...prevVehicle, [currentImageType]: newImageList };
    });

    event.target.value = '';
  }, [currentImageType]);

  const handleVehicleChange = useCallback((updatedVehicle: Vehicle) => {
    setVehicle(updatedVehicle);
  }, []);

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleSave = useCallback(async () => {
    if (!onSave) {
      console.warn("UploadAsset: handleSave was called but no onSave prop was provided.");
      return;
    }
    if (uploadedFiles.length === 0) {
      setNotification({ open: true, message: "No changes to save", severity: "info" });
      return;
    }
    setIsSaving(true);
    try {
      await onSave({ vehicle, uploadedFiles });
      setNotification({ open: true, message: "Changes saved successfully", severity: "success" });
      setUploadedFiles([]);
    } catch (error) {
      console.error("Error saving changes:", error);
      setNotification({ open: true, message: "Error saving changes. Please try again.", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  }, [onSave, vehicle, uploadedFiles]);

  return {
    vehicle,
    uploadedFiles,
    currentImageType,
    isSaving,
    notification,
    setCurrentImageType,
    handleFileUpload,
    handleVehicleChange,
    handleCloseNotification,
    handleSave,
  };
};